import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";

import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";

import { auth } from "./firebase";

import "./LoginModel.css";

const LoginModel = () => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");

  const [confirmationResult, setConfirmationResult] =
    useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ============================================
  // FIRST VISIT + PROFILE ICON CLICK
  // ============================================

  useEffect(() => {
    const isFirstVisit =
      !localStorage.getItem("firstVisit");

    if (isFirstVisit) {
      setShow(true);
      localStorage.setItem(
        "firstVisit",
        "no"
      );
    }

    // Navbar profile icon click
    const openLoginModal = () => {
      setShow(true);
      setStep(1);
      setOtp("");
      setError("");
    };

    window.addEventListener(
      "openLoginModal",
      openLoginModal
    );

    return () => {
      window.removeEventListener(
        "openLoginModal",
        openLoginModal
      );
    };
  }, []);

  // ============================================
  // CREATE RECAPTCHA
  // ============================================

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier =
        new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          {
            size: "invisible",

            callback: () => {
              console.log(
                "reCAPTCHA verified"
              );
            },

            "expired-callback": () => {
              if (
                window.recaptchaVerifier
              ) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier =
                  null;
              }
            },
          }
        );
    }
  };

  // ============================================
  // SEND OTP
  // ============================================

  const handleSendOtp = async (e) => {
    e.preventDefault();

    setError("");

    // Validate Indian number
    if (!phone.match(/^[6-9]\d{9}$/)) {
      setError(
        "Please enter a valid 10-digit Indian mobile number."
      );
      return;
    }

    try {
      setLoading(true);

      setupRecaptcha();

      const appVerifier =
        window.recaptchaVerifier;

      // Add India country code
      const formattedPhone =
        `+91${phone}`;

      // Firebase sends real OTP
      const result =
        await signInWithPhoneNumber(
          auth,
          formattedPhone,
          appVerifier
        );

      setConfirmationResult(result);

      setStep(2);

      setError("");

      alert(
        `OTP sent to +91 ${phone}`
      );
    } catch (error) {
      console.error(
        "Firebase OTP Error:",
        error
      );

      setError(
        error.message ||
          "Unable to send OTP. Please try again."
      );

      // Reset reCAPTCHA
      if (
        window.recaptchaVerifier
      ) {
        window.recaptchaVerifier.clear();

        window.recaptchaVerifier =
          null;
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // VERIFY OTP
  // ============================================

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    setError("");

    if (!otp.match(/^\d{6}$/)) {
      setError(
        "Please enter a valid 6-digit OTP."
      );
      return;
    }

    if (!confirmationResult) {
      setError(
        "Please request OTP again."
      );
      return;
    }

    try {
      setLoading(true);

      const result =
        await confirmationResult.confirm(
          otp
        );

      const user = result.user;

      console.log(
        "Firebase user:",
        user
      );

      console.log(
        "User UID:",
        user.uid
      );

      console.log(
        "Phone:",
        user.phoneNumber
      );

      alert(
        "OTP verified successfully! 🎉"
      );

      // Close modal
      setShow(false);

      // Reset states
      setStep(1);
      setPhone("");
      setOtp("");
      setConfirmationResult(
        null
      );

    } catch (error) {
      console.error(
        "OTP Verification Error:",
        error
      );

      setError(
        "Invalid OTP. Please check your OTP and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // CLOSE MODAL
  // ============================================

  const handleClose = () => {
    setShow(false);

    setError("");

    setOtp("");

    setStep(1);
  };

  // ============================================
  // CHANGE MOBILE NUMBER
  // ============================================

  const handleChangeNumber = () => {
    setStep(1);

    setOtp("");

    setError("");

    setConfirmationResult(
      null
    );

    if (
      window.recaptchaVerifier
    ) {
      window.recaptchaVerifier.clear();

      window.recaptchaVerifier =
        null;
    }
  };

  return (
    <>
      <Modal
        show={show}
        centered
        onHide={handleClose}
      >

        {/* HEADER */}
        <Modal.Header closeButton>
          <Modal.Title>
            User Login
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>

          {/* ERROR */}
          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          {/* ================================= */}
          {/* STEP 1 - MOBILE NUMBER */}
          {/* ================================= */}

          {step === 1 && (
            <Form
              onSubmit={handleSendOtp}
            >

              <Form.Group
                className="mb-3"
                controlId="formPhone"
              >

                <Form.Label>
                  Enter your Mobile Number
                </Form.Label>

                <Form.Control
                  type="tel"
                  placeholder="Enter 10-digit number"
                  value={phone}
                  maxLength={10}
                  onChange={(e) => {
                    const value =
                      e.target.value.replace(
                        /\D/g,
                        ""
                      );

                    setPhone(value);
                  }}
                />

                <Form.Text className="text-muted">
                  We will send an OTP to your
                  mobile number.
                </Form.Text>

              </Form.Group>

              <Button
                variant="primary"
                className="w-100"
                type="submit"
                disabled={loading}
              >

                {loading ? (
                  <>
                    <Spinner
                      size="sm"
                      animation="border"
                      className="me-2"
                    />

                    Sending OTP...
                  </>
                ) : (
                  "Send OTP"
                )}

              </Button>

            </Form>
          )}

          {/* ================================= */}
          {/* STEP 2 - OTP */}
          {/* ================================= */}

          {step === 2 && (
            <Form
              onSubmit={handleVerifyOtp}
            >

              <Form.Group
                className="mb-3"
                controlId="formOtp"
              >

                <Form.Label>
                  Enter OTP
                </Form.Label>

                <Form.Control
                  type="tel"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  maxLength={6}
                  onChange={(e) => {
                    const value =
                      e.target.value.replace(
                        /\D/g,
                        ""
                      );

                    setOtp(value);
                  }}
                />

              </Form.Group>

              <p className="text-muted small">
                OTP sent to +91 {phone}
              </p>

              <Button
                variant="success"
                className="w-100"
                type="submit"
                disabled={loading}
              >

                {loading ? (
                  <>
                    <Spinner
                      size="sm"
                      animation="border"
                      className="me-2"
                    />

                    Verifying...
                  </>
                ) : (
                  "Verify OTP"
                )}

              </Button>

              <Button
                variant="link"
                className="w-100 mt-2"
                type="button"
                onClick={
                  handleChangeNumber
                }
              >
                Change Mobile Number
              </Button>

            </Form>
          )}

          {/* Firebase reCAPTCHA */}
          <div
            id="recaptcha-container"
          ></div>

        </Modal.Body>

        <Modal.Footer>

          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Close
          </Button>

        </Modal.Footer>

      </Modal>
    </>
  );
};

export default LoginModel;
